import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import styled from 'styled-components';
import { useApp } from '../../AppContext';
import { loginFunc, setSessionFunc } from '../../api/func/userFunc';

const MobileLoginContainer = styled.div`
  width: 100vw;
  min-height: 100vh;
  background: linear-gradient(135deg, #1e3c72 0%, #2a5298 100%);
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  padding: 0;
  margin: 0;
  box-sizing: border-box;
`;

const LoginCard = styled.div`
  width: 90vw;
  max-width: 500px;
  background: transparent;
  border: 2px solid rgba(255, 255, 255, 0.5);
  border-radius: 20px;
  backdrop-filter: blur(15px);
  padding: 3rem 2rem;
  box-shadow: 0 8px 32px rgba(0, 0, 0, 0.1);
  box-sizing: border-box;
`;

const Logo = styled.div`
  text-align: center;
  margin-bottom: 2rem;
  
  img {
    width: 120px;
    height: auto;
  }
  
  h1 {
    margin-top: 1rem;
    color: #fff;
    font-size: 2rem;
    font-weight: 600;
  }
`;

const Form = styled.form`
  display: flex;
  flex-direction: column;
  gap: 1rem;
`;

const InputGroup = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
`;

const Label = styled.label`
  color: #fff;
  font-size: 0.9rem;
  font-weight: 500;
`;

const Input = styled.input`
  padding: 1rem 0.5rem;
  background: transparent;
  border: none;
  border-bottom: 2px solid #fff;
  color: #fff;
  font-size: 1.1rem;
  width: 100%;
  transition: all 0.3s ease;
  
  &::placeholder {
    color: rgba(255, 255, 255, 0.7);
    font-size: 1rem;
  }
  
  &:focus {
    outline: none;
    border-bottom-color: dodgerblue;
  }
  
  &:-webkit-autofill {
    -webkit-box-shadow: 0 0 0px 1000px transparent inset !important;
    box-shadow: 0 0 0px 1000px transparent inset !important;
    -webkit-text-fill-color: white !important;
    transition: background-color 5000s ease-in-out 0s;
  }
`;

const Button = styled.button`
  padding: 1.2rem;
  background: dodgerblue;
  color: white;
  border: none;
  border-radius: 5px;
  font-size: 1.1rem;
  font-weight: 600;
  letter-spacing: 2px;
  cursor: pointer;
  transition: all 0.3s ease;
  margin-top: 1.5rem;
  box-shadow: 3px 0 10px rgba(0, 0, 0, 0.5);
  width: 100%;
  
  &:hover {
    transform: translateY(-2px);
    box-shadow: 0 10px 20px rgba(30, 144, 255, 0.3);
  }
  
  &:disabled {
    opacity: 0.6;
    cursor: not-allowed;
  }
`;

const ErrorMessage = styled.div`
  color: rgb(41, 166, 224);
  font-size: 0.9rem;
  margin-top: 0.5rem;
  text-align: center;
  font-weight: 600;
`;

const RememberMe = styled.div`
  display: flex;
  align-items: center;
  gap: 0.5rem;
  margin-top: 0.5rem;
  
  input {
    width: auto;
  }
  
  label {
    color: #fff;
    font-size: 0.9rem;
  }
`;

const Links = styled.div`
  display: flex;
  justify-content: center;
  margin-top: 1.5rem;
  
  a {
    color: #fff;
    text-decoration: none;
    font-size: 0.9rem;
    
    &:hover {
      text-decoration: underline;
    }
  }
`;

const DomainSelect = styled.select`
  width: 100%;
  padding: 1rem 0.5rem;
  background: transparent;
  border: none;
  border-bottom: 2px solid #fff;
  color: #fff;
  font-size: 1.1rem;
  appearance: none;
  cursor: pointer;
  transition: all 0.3s ease;
  
  &:focus {
    outline: none;
    border-bottom-color: dodgerblue;
  }
  
  option {
    background: #1e3c72;
    color: white;
    font-size: 1rem;
  }
`;

const MobileLogin = () => {
  const [id, setId] = useState('');
  const [password, setPassword] = useState('');
  const [domain, setDomain] = useState('antsnest');
  const [rememberMe, setRememberMe] = useState(false);
  const [error, setError] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  
  const { login } = useApp();
  const navigate = useNavigate();

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setIsLoading(true);

    try {
      const result = await loginFunc(id, password, domain);
      
      if (result && result.code === 200) {
        // PC 버전과 동일하게 처리
        login(result); // 전체 result 객체를 전달
        console.log(result);
        
        // 세션 서버 저장 (PC 버전과 동일)
        const setSession = await setSessionFunc(result);
        
        if (setSession && setSession.code === 200) {
          // 서버 세션에 데이터 저장 완료
          if (rememberMe) {
            localStorage.setItem('rememberedId', id);
          } else {
            localStorage.removeItem('rememberedId');
          }
          navigate('/');
        } else {
          // 서버 세션에 데이터 저장 실패
          setError('서버 확인이 필요합니다.');
        }
      } else {
        setError('아이디 또는 비밀번호가 올바르지 않습니다.');
      }
    } catch (err) {
      console.error('로그인 오류:', err);
      setError('로그인 중 오류가 발생했습니다.');
    } finally {
      setIsLoading(false);
    }
  };

  React.useEffect(() => {
    const rememberedId = localStorage.getItem('rememberedId');
    if (rememberedId) {
      setId(rememberedId);
      setRememberMe(true);
    }
  }, []);

  return (
    <MobileLoginContainer>
      <LoginCard>
        <Logo>
          <h1>Log In</h1>
        </Logo>
        
        <Form onSubmit={handleSubmit}>
          <InputGroup>
            <Input
              type="text"
              id="id"
              value={id}
              onChange={(e) => setId(e.target.value)}
              placeholder="ID"
              required
              autoComplete="username"
            />
          </InputGroup>
          
          <InputGroup>
            <Input
              type="password"
              id="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              placeholder="비밀번호"
              required
              autoComplete="current-password"
            />
          </InputGroup>
          
          <InputGroup>
            <DomainSelect
              value={domain}
              onChange={(e) => setDomain(e.target.value)}
            >
              <option value="antsnest">앤츠네스트</option>
            </DomainSelect>
          </InputGroup>
          
          <RememberMe>
            <input
              type="checkbox"
              id="rememberMe"
              checked={rememberMe}
              onChange={(e) => setRememberMe(e.target.checked)}
            />
            <label htmlFor="rememberMe">아이디 저장</label>
          </RememberMe>
          
          {error && <ErrorMessage>{error}</ErrorMessage>}
          
          <Button type="submit" disabled={isLoading}>
            {isLoading ? '로그인 중...' : '로그인'}
          </Button>
        </Form>
        
        <Links>
          <a href="/register">회원가입</a>
        </Links>
      </LoginCard>
    </MobileLoginContainer>
  );
};

export default MobileLogin;